import moment from "moment";

/**
 * Convert a local Date object to a UTC date string (YYYY-MM-DD).
 * Used when storing scheduled_date to the database.
 */
export function toUTCDate(localDate: Date): string {
  return moment(localDate).utc().format("YYYY-MM-DD");
}

/**
 * Convert a local time string ("HH:mm" or "HH:mm:ss") on a given local date
 * to a UTC time string ("HH:mm:ss").
 * Used when storing scheduled_time to the database.
 */
export function toUTCTime(localTimeStr: string, localDate: Date): string {
  const [hours, minutes] = localTimeStr.split(":");
  const local = moment(localDate)
    .hours(parseInt(hours))
    .minutes(parseInt(minutes))
    .seconds(0);
  return local.utc().format("HH:mm:ss");
}

/**
 * Convert a local time string ("HH:mm" or "HH:mm:ss") on a given local date
 * to a UTC date string ("YYYY-MM-DD").
 * The date may shift when converting to UTC (e.g. 11 PM local → next day UTC).
 */
export function toUTCDateFromLocalTime(
  localTimeStr: string,
  localDate: Date
): string {
  const [hours, minutes] = localTimeStr.split(":");
  const local = moment(localDate)
    .hours(parseInt(hours))
    .minutes(parseInt(minutes))
    .seconds(0);
  return local.utc().format("YYYY-MM-DD");
}

/**
 * Display a UTC date string in the user's local timezone.
 * @param utcDateStr - "YYYY-MM-DD" date stored in UTC
 * @param formatStr - moment format string, defaults to "MMM D, YYYY"
 */
export function displayDate(
  utcDateStr: string,
  formatStr = "MMM D, YYYY"
): string {
  return moment.utc(utcDateStr, "YYYY-MM-DD").local().format(formatStr);
}

/**
 * Display a UTC time + date in the user's local timezone.
 * @param utcTimeStr - "HH:mm:ss" time stored in UTC
 * @param utcDateStr - "YYYY-MM-DD" date stored in UTC
 * @param formatStr - moment format string, defaults to "h:mm A"
 */
export function displayTime(
  utcTimeStr: string,
  utcDateStr: string,
  formatStr = "h:mm A"
): string {
  const utcDateTime = `${utcDateStr}T${utcTimeStr}`;
  return moment.utc(utcDateTime).local().format(formatStr);
}

/**
 * Display a combined UTC date+time as a full local datetime string.
 */
export function displayDateTime(
  utcDateStr: string,
  utcTimeStr: string,
  formatStr = "MMM D, YYYY [at] h:mm A"
): string {
  const utcDateTime = `${utcDateStr}T${utcTimeStr}`;
  return moment.utc(utcDateTime).local().format(formatStr);
}

/**
 * Get today's date in UTC format ("YYYY-MM-DD").
 */
export function todayUTC(): string {
  return moment.utc().format("YYYY-MM-DD");
}

/**
 * Get today's date in local format ("YYYY-MM-DD").
 */
export function todayLocal(): string {
  return moment().format("YYYY-MM-DD");
}

/**
 * Check if a UTC date+time is in the past relative to now.
 */
export function isSlotInPast(utcDateStr: string, utcTimeStr: string): boolean {
  const utcDateTime = moment.utc(`${utcDateStr}T${utcTimeStr}`);
  return utcDateTime.isBefore(moment.utc());
}

/**
 * Generate time slots for a given provider's working hours.
 *
 * - Slots are generated in provider-LOCAL time (startHour/endHour are local hours).
 * - Each slot's stored `hour24` value is converted to UTC for DB storage.
 * - Each slot's `utcDate` may differ from the local date when the conversion crosses midnight.
 * - Display labels remain in local time so the user sees familiar times.
 * - `bookedTimesUTC` should be UTC time strings already stored in the DB.
 *
 * @param startHour - provider's local start hour (e.g. 9)
 * @param endHour - provider's local end hour (e.g. 17)
 * @param localDate - the local date the user selected on the calendar
 * @param bookedTimesUTC - array of UTC "HH:mm:ss" strings already booked
 */
export function generateTimeSlotsUTC(
  startHour: number,
  endHour: number,
  localDate: Date,
  bookedTimesUTC: string[]
) {
  const slots: {
    time: string; // display string, e.g. "2:00 PM"
    hour24: string; // UTC time "HH:mm:ss"
    utcDate: string; // UTC date "YYYY-MM-DD" (may differ from localDate)
    available: boolean;
  }[] = [];

  const now = moment();
  const selectedLocal = moment(localDate).startOf("day");
  const isToday = selectedLocal.isSame(now, "day");

  for (let hour = startHour; hour < endHour; hour++) {
    for (const min of [0, 30]) {
      // Build the local datetime for this slot
      const slotLocal = moment(localDate)
        .hours(hour)
        .minutes(min)
        .seconds(0)
        .milliseconds(0);

      // Skip past times if booking for today
      if (isToday && slotLocal.isBefore(now)) continue;

      // Convert to UTC for storage
      const slotUTC = slotLocal.clone().utc();
      const utcTime = slotUTC.format("HH:mm:ss");
      const utcDate = slotUTC.format("YYYY-MM-DD");

      // Display in local time
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? "PM" : "AM";
      const timeDisplay = `${displayHour}:${min.toString().padStart(2, "0")} ${ampm}`;

      const isBooked = bookedTimesUTC.includes(utcTime);

      slots.push({
        time: timeDisplay,
        hour24: utcTime,
        utcDate,
        available: !isBooked,
      });
    }
  }

  return slots;
}
